import { QA_SIDECAR_DEFAULT } from "./types";
import type { QaAction } from "./types";

export function sidecarBase(): string {
  if (typeof window === "undefined") return QA_SIDECAR_DEFAULT;
  const { protocol, hostname, port } = window.location;
  if (protocol === "http:" && (hostname === "127.0.0.1" || hostname === "localhost")) {
    return "/qa-api";
  }
  if (hostname === "127.0.0.1" || hostname === "localhost") {
    return QA_SIDECAR_DEFAULT;
  }
  return QA_SIDECAR_DEFAULT;
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const r = await fetch(`${sidecarBase()}${path}`, {
    ...init,
    headers: {
      Accept: "application/json",
      ...(init?.body ? { "Content-Type": "application/json" } : {}),
      ...(init?.headers || {}),
    },
  });
  const text = await r.text();
  let body: unknown = null;
  try {
    body = text ? JSON.parse(text) : null;
  } catch {
    body = { error: text };
  }
  if (!r.ok) {
    const err = body as { error?: string };
    throw new Error(err?.error || `sidecar ${r.status}`);
  }
  return body as T;
}

export type SidecarHealth = {
  ok: boolean;
  playwright: boolean;
  grok: boolean;
};

export function probeSidecar(): Promise<SidecarHealth> {
  return req<SidecarHealth>("/health");
}

export function startBrowserSession(
  targetUrl: string,
  repoPath?: string,
): Promise<{ sessionId: string }> {
  return req("/session/start", {
    method: "POST",
    body: JSON.stringify({ targetUrl, repoPath: repoPath || undefined }),
  });
}

export function hardResetSession(sessionId: string, targetUrl: string): Promise<{ ok: boolean }> {
  return req("/session/reset", {
    method: "POST",
    body: JSON.stringify({ sessionId, targetUrl }),
  });
}

export function stopBrowserSession(sessionId: string): Promise<{ ok: boolean }> {
  return req("/session/stop", {
    method: "POST",
    body: JSON.stringify({ sessionId }),
  });
}

export type RunTaskResult = {
  ok: boolean;
  screenshotPath?: string;
  error?: string;
};

export function runSidecarTask(opts: {
  sessionId: string;
  itemId: string;
  title: string;
  actions: QaAction[];
}): Promise<RunTaskResult> {
  return req("/session/run", {
    method: "POST",
    body: JSON.stringify(opts),
  });
}

export type GrokJob = { jobId: string; running: boolean; exitCode?: number | null };

export function openGrokWindow(opts: {
  screenshotPath: string;
  repoPath: string;
  itemId?: string;
  title?: string;
  error?: string;
  targetUrl?: string;
  pageUrl?: string;
  actions?: QaAction[];
}): Promise<GrokJob> {
  return req("/grok/open", {
    method: "POST",
    body: JSON.stringify(opts),
  });
}

export function grokJobStatus(jobId: string): Promise<GrokJob> {
  return req(`/grok/${encodeURIComponent(jobId)}`);
}

export function sleep(ms: number, shouldStop: () => boolean): Promise<void> {
  return new Promise((resolve) => {
    const t0 = Date.now();
    const tick = () => {
      if (shouldStop() || Date.now() - t0 >= ms) resolve();
      else requestAnimationFrame(tick);
    };
    tick();
  });
}

export async function waitGrokJob(
  jobId: string,
  shouldStop: () => boolean,
  timeoutMs = 15 * 60 * 1000,
): Promise<GrokJob> {
  const t0 = Date.now();
  while (!shouldStop() && Date.now() - t0 < timeoutMs) {
    const st = await grokJobStatus(jobId);
    if (!st.running) return st;
    await sleep(1500, shouldStop);
  }
  return grokJobStatus(jobId);
}
